import { useCallback, useEffect, useState } from 'react';
import Layout from '../../components/Layout';
import { Card, CardContent } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Select } from '../../components/ui/Select';
import { clientPortalAPI } from '../../api';
import { useToast } from '../../hooks/useToast';
import { Loader2, Printer, Download } from 'lucide-react';

const PERIODS = [
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
  { value: '1m', label: 'Last month' },
  { value: '3m', label: 'Last 3 months' },
  { value: '6m', label: 'Last 6 months' },
  { value: '12m', label: 'Last 12 months' },
  { value: 'all', label: 'All time' },
  { value: 'custom', label: 'Custom range' },
];

export default function ClientStatement() {
  const { success, error: showError } = useToast();
  const [period, setPeriod] = useState('12m');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [hidePaidInvoices, setHidePaidInvoices] = useState(false);
  const [hidePayments, setHidePayments] = useState(true);
  const [previewUrl, setPreviewUrl] = useState('');
  const [loading, setLoading] = useState(false);

  const buildParams = useCallback(() => {
    const params = {
      period,
      hidePaidInvoices: String(hidePaidInvoices),
      hidePaymentsColumn: String(hidePayments),
    };
    if (period === 'custom') {
      params.customFrom = customFrom;
      params.customTo = customTo;
    }
    return params;
  }, [period, customFrom, customTo, hidePaidInvoices, hidePayments]);

  const refreshPreview = useCallback(async () => {
    setLoading(true);
    try {
      const html = await clientPortalAPI.getMyStatementHtml(buildParams());
      const blob = new Blob([html], { type: 'text/html' });
      setPreviewUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return URL.createObjectURL(blob);
      });
    } catch (e) {
      showError(e.message || 'Could not load statement');
    } finally {
      setLoading(false);
    }
  }, [buildParams, showError]);

  useEffect(() => {
    refreshPreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- initial preview only
  }, []);

  const handlePrint = () => {
    if (!previewUrl) return;
    const w = window.open(previewUrl, '_blank', 'noopener,noreferrer');
    if (w) w.addEventListener('load', () => w.print());
  };

  const handleDownload = async () => {
    try {
      const blob = await clientPortalAPI.getMyStatementPdfBlob(buildParams());
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'statement.pdf';
      a.click();
      URL.revokeObjectURL(url);
      success('Download started');
    } catch (e) {
      showError(e.message || 'Download failed');
    }
  };

  return (
    <Layout>
      <div className="bg-[var(--ds-surface-panel)] border-b border-[var(--ds-border-subtle)] shadow-[var(--ds-shadow-card)]">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <h1 className="text-display text-[var(--ds-text-primary)] font-bold">Statement of account</h1>
          <p className="mt-1 text-meta text-[var(--ds-text-secondary)]">
            Choose dates and options, then preview, print, or download PDF.
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8 space-y-6">
        <Card>
          <CardContent className="p-6 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-[var(--ds-text-secondary)] mb-1">Period</label>
                <Select value={period} onChange={(e) => setPeriod(e.target.value)}>
                  {PERIODS.map((p) => (
                    <option key={p.value} value={p.value}>
                      {p.label}
                    </option>
                  ))}
                </Select>
              </div>
              {period === 'custom' && (
                <>
                  <div>
                    <label className="block text-sm font-medium mb-1">From</label>
                    <input
                      type="date"
                      className="w-full rounded-md border border-[var(--ds-border-subtle)] bg-background px-3 py-2 text-sm"
                      value={customFrom}
                      onChange={(e) => setCustomFrom(e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">To</label>
                    <input
                      type="date"
                      className="w-full rounded-md border border-[var(--ds-border-subtle)] bg-background px-3 py-2 text-sm"
                      value={customTo}
                      onChange={(e) => setCustomTo(e.target.value)}
                    />
                  </div>
                </>
              )}
            </div>
            <div className="flex flex-wrap gap-6">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={hidePaidInvoices}
                  onChange={(e) => setHidePaidInvoices(e.target.checked)}
                />
                Hide paid invoices
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={hidePayments}
                  onChange={(e) => setHidePayments(e.target.checked)}
                />
                Hide payments column
              </label>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button type="button" variant="outline" onClick={refreshPreview} disabled={loading}>
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Update preview'}
              </Button>
              <Button type="button" variant="outline" onClick={handlePrint} disabled={!previewUrl}>
                <Printer className="h-4 w-4 mr-2" />
                Print
              </Button>
              <Button type="button" onClick={handleDownload}>
                <Download className="h-4 w-4 mr-2" />
                Download PDF
              </Button>
            </div>
          </CardContent>
        </Card>

        {previewUrl && (
          <Card>
            <CardContent className="p-0 overflow-hidden rounded-[inherit]">
              <iframe title="Statement preview" src={previewUrl} className="w-full min-h-[600px] border-0" />
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
}
